/**
 * De creditnota zoals Taco hem wil zien (voorbeeld aangeleverd 24-09-2026).
 *
 * **Waarom een eigen renderer en niet een vlaggetje op `renderInvoiceHtml`.** Eerst zat het als
 * optie in de factuur-renderer, vanuit de gedachte "een creditnota is een factuur in de min". Het
 * aangeleverde voorbeeld laat zien dat de opbouw op vier punten echt anders is:
 *  - de verzendkosten staan als REGEL in de tabel (aantal −1), niet in het totalenblok;
 *  - de aantallen zijn negatief;
 *  - er staat een apart blok "Reden creditnota" naast de klantgegevens;
 *  - eronder staan twee verplichte administratieve alinea's die op een factuur niet thuishoren.
 * Dat met `if (credit)` door de factuur heen weven maakt de factuur — het document waar het geld
 * aan hangt — moeilijker te lezen en risicovoller om aan te raken. Vandaar een eigen bestand met
 * dezelfde typografie.
 *
 * Alle styling is INLINE: deze HTML gaat ook als mailbody de deur uit, en e-mailclients strippen
 * <style>-blokken.
 *
 * Bedragen komen hier POSITIEF binnen (zoals ze in `CreditNote` staan). Het minteken wordt hier
 * gezet, zodat er nergens anders in de keten met negatieve bedragen gerekend hoeft te worden.
 */
import { COMPANY, isPlaceholder } from "../company";
import { BTW_VERLEGD_LABEL } from "../pricing/btw-verlegging";
import { orderLabel } from "../types/order";
import type { CreditNote, Order } from "../types/order";

const NAVY = "#013878";

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] ?? c));

const eurFmt = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" });
/** Bedrag in de min, zoals het op een creditnota hoort: "− € 197,76". */
const min = (cents: number): string => (cents === 0 ? eurFmt.format(0) : `− ${eurFmt.format(cents / 100)}`);
/** Bedrag zonder teken — voor een stuksprijs, die een tarief is en geen bedrag dat verrekend wordt. */
const plus = (cents: number): string => eurFmt.format(cents / 100);

/** Datum altijd in Nederlandse tijd: deze HTML wordt ook op de server (UTC) gerenderd. */
function datum(ms: number | undefined): string {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString("nl-NL", { timeZone: "Europe/Amsterdam", day: "numeric", month: "long", year: "numeric" });
}

export interface CreditNoteRenderOptions {
  /** Absolute logo-URL. Leeg = de bedrijfsnaam als tekst. */
  logoUrl?: string;
  /** Start de printdialoog zodra de pagina er is (admin-download). Uit in de mail. */
  autoPrint?: boolean;
  /** Artikelnummer per orderregel — komt onder de omschrijving te staan. */
  articleNumberFor?: (item: Order["items"][number]) => string | undefined;
}

/**
 * De alinea "Verrekening". Bij een volledige creditering staat er iets anders dan bij een deel —
 * en dat verschil moet kloppen, want dit is de tekst waar een boekhouder op afgaat.
 */
function verrekeningTekst(order: Order, nota: CreditNote, volledig: boolean): string {
  const factuur = orderLabel(order);
  const betaald = order.paidAt ? datum(order.paidAt) : datum(order.createdAt);
  const bedrag = eurFmt.format(nota.totals.total / 100);
  const kop = volledig
    ? `Deze creditnota crediteert de volledige waarde van factuur ${factuur}.`
    : `Deze creditnota crediteert een deel van factuur ${factuur}.`;
  const voldaan = betaald ? ` De oorspronkelijke factuur is op ${betaald} als voldaan geregistreerd.` : "";
  /*
   * Is het geld al terug, zeg dat dan op de nota zelf. Bij order 1025 kreeg de klant € 260,71 op
   * zijn rekening zonder enige uitleg, omdat de nota niet gemaild was en een terugbetaling geen
   * eigen bericht stuurt (25-09-2026). Dit is de plek waar die mededeling hoort: op het boekstuk
   * dat hij tóch al moet inboeken.
   */
  const terug = nota.refundedAt
    ? ` Het bedrag is op ${datum(nota.refundedAt)} teruggestort op de rekening waarmee u betaald heeft.`
    : " Het bedrag wordt teruggestort op de rekening waarmee u betaald heeft.";
  return `${kop}${voldaan} Het gecrediteerde bedrag bedraagt derhalve ${bedrag}.${terug}`;
}

export function renderCreditNoteHtml(order: Order, nota: CreditNote, opts: CreditNoteRenderOptions = {}): string {
  const factuurNo = orderLabel(order);
  const verlegd = order.btw?.verlegd === true;
  const volledig = (nota.totals.total ?? 0) >= (order.totals?.total ?? 0);

  // ---- Bedrijfsblok ----
  const coAddr = [COMPANY.street, `${COMPANY.postalCode} ${COMPANY.city}`.trim(), COMPANY.country].filter((l) => !isPlaceholder(l));
  const coLine2 = [!isPlaceholder(COMPANY.kvk) ? `KvK ${esc(COMPANY.kvk)}` : "", !isPlaceholder(COMPANY.vat) ? `Btw ${esc(COMPANY.vat)}` : ""].filter(Boolean).join(" · ");
  const coLine3 = [!isPlaceholder(COMPANY.email) ? esc(COMPANY.email) : "", !isPlaceholder(COMPANY.phone) ? esc(COMPANY.phone) : ""].filter(Boolean).join(" · ");
  const bedrijf = [
    opts.logoUrl
      ? `<img src="${esc(opts.logoUrl)}" alt="Glastotaal" style="height:40px;width:auto;display:block;margin-bottom:8px;">`
      : `<strong style="font-size:13px;letter-spacing:.04em;">${esc(COMPANY.legalName.toUpperCase())}</strong>`,
    ...coAddr.map(esc),
  ].join("<br>");

  // ---- Klant ----
  const c = order.customer;
  const a = c?.address;
  const klant = [
    c?.companyName ? esc(c.companyName) : "",
    esc(`${c?.firstName ?? ""} ${c?.lastName ?? ""}`.trim()),
    a ? esc(a.street) : "",
    a ? esc(`${a.postalCode} ${a.city}`.trim()) : "",
    a?.country === "BE" ? "België" : "",
    c?.email ? esc(c.email) : "",
    c?.kvk ? `KvK ${esc(c.kvk)}` : "",
    c?.vatNumber ? `Btw ${esc(c.vatNumber)}` : "",
  ].filter(Boolean).join("<br>");

  // ---- Kopgegevens rechts ----
  const kopRij = (label: string, waarde: string): string =>
    `<tr><td style="padding:1px 8px 1px 0;text-align:right;color:#374151;">${esc(label)}:</td><td style="padding:1px 0;text-align:right;font-weight:700;color:#111827;white-space:nowrap;">${esc(waarde)}</td></tr>`;
  const kop = [
    kopRij("Creditnotanummer", nota.number),
    kopRij("Creditnotadatum", datum(nota.at)),
    kopRij("Betreft factuur", factuurNo),
    order.createdAt ? kopRij("Factuurdatum", datum(order.createdAt)) : "",
  ].join("");

  // ---- Regels ----
  const cellL = "padding:10px 8px;border-bottom:1px solid #e5e7eb;vertical-align:top;";
  const cellR = "padding:10px 8px;border-bottom:1px solid #e5e7eb;vertical-align:top;text-align:right;white-space:nowrap;";
  const regels = nota.lines.map((r) => {
    const artikelNr = opts.articleNumberFor?.(order.items?.[r.itemIndex] ?? ({} as Order["items"][number]));
    return `<tr>
      <td style="${cellL}">${esc(r.productName)}${artikelNr ? `<div style="color:#6b7280;font-size:11px;margin-top:2px;">Artikelnr. ${esc(artikelNr)}</div>` : ""}</td>
      <td style="${cellR}">−${r.quantity}</td>
      <td style="${cellR}">${plus(r.unitPriceExclCents)}</td>
      <td style="${cellR}">${min(r.unitPriceExclCents * r.quantity)}</td>
    </tr>`;
  });
  // Verzendkosten als eigen regel — zo ziet de klant (en de boekhouder) meteen dat ze meegaan in
  // de creditering, of juist niet.
  if (nota.shippingExclCents > 0) {
    regels.push(`<tr>
      <td style="${cellL}">Verzendkosten</td>
      <td style="${cellR}">−1</td>
      <td style="${cellR}">${plus(nota.shippingExclCents)}</td>
      <td style="${cellR}">${min(nota.shippingExclCents)}</td>
    </tr>`);
  }

  const th = `text-align:right;border-bottom:2px solid ${NAVY};padding:8px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#374151;background:#f3f4f6;`;
  const totRij = (label: string, waarde: string, vet = false) =>
    `<tr>
      <td style="padding:7px 8px;${vet ? `font-weight:700;color:${NAVY};font-size:14px;border-top:2px solid ${NAVY};` : "border-bottom:1px solid #e5e7eb;"}">${esc(label)}</td>
      <td style="padding:7px 8px;text-align:right;white-space:nowrap;${vet ? `font-weight:700;color:${NAVY};font-size:14px;border-top:2px solid ${NAVY};` : "border-bottom:1px solid #e5e7eb;"}">${waarde}</td>
    </tr>`;

  const blok = (titel: string, tekst: string) =>
    `<div style="margin-top:18px;"><div style="font-weight:700;color:${NAVY};font-size:12px;">${esc(titel)}</div>
      <div style="margin-top:3px;color:#374151;line-height:1.55;font-size:12px;">${esc(tekst)}</div></div>`;

  const head = `<style>@media print{body{padding:0!important;}}</style>`;
  const printHook = opts.autoPrint ? `<script>window.addEventListener("load",function(){window.print()})</script>` : "";

  return `<!doctype html><html lang="nl"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Creditnota ${esc(nota.number)}</title>${head}</head>
<body style="font-family:Arial,Helvetica,sans-serif;color:#1f2937;margin:0;padding:40px;font-size:13px;background:#fff;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
    <td style="vertical-align:top;line-height:1.5;font-size:12px;">${bedrijf}
      ${coLine2 ? `<div style="margin-top:10px;color:#4b5563;">${coLine2}</div>` : ""}
      ${coLine3 ? `<div style="color:#4b5563;">${coLine3}</div>` : ""}
    </td>
    <td style="vertical-align:top;text-align:right;">
      <div style="font-size:26px;font-weight:700;letter-spacing:.02em;color:#111827;">CREDITNOTA</div>
      <table role="presentation" cellpadding="0" cellspacing="0" style="margin-top:12px;margin-left:auto;font-size:12px;">${kop}</table>
    </td>
  </tr></table>

  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:26px;border-collapse:collapse;">
    <tr>
      <td style="width:50%;vertical-align:top;border:1px solid #d1d5db;padding:12px;">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#374151;margin-bottom:6px;">Factuur aan</div>
        <div style="line-height:1.55;">${klant || "—"}</div>
      </td>
      <td style="width:50%;vertical-align:top;border:1px solid #d1d5db;border-left:0;padding:12px;">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#374151;margin-bottom:6px;">Reden creditnota</div>
        <div style="line-height:1.55;">${esc(nota.reason || (volledig ? `Volledige creditering van factuur ${factuurNo}.` : `Gedeeltelijke creditering van factuur ${factuurNo}.`))}</div>
      </td>
    </tr>
  </table>

  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:22px;border-collapse:collapse;">
    <tr>
      <th style="${th}text-align:left;">Omschrijving</th>
      <th style="${th}">Aantal</th>
      <th style="${th}">Stuksprijs</th>
      <th style="${th}">Totaal</th>
    </tr>
    ${regels.join("")}
  </table>

  <table role="presentation" cellpadding="0" cellspacing="0" style="margin-top:14px;margin-left:auto;width:340px;border-collapse:collapse;">
    ${totRij("Subtotaal (excl. btw)", min(nota.totals.subtotal + nota.totals.shipping))}
    ${verlegd ? totRij(BTW_VERLEGD_LABEL, eurFmt.format(0)) : totRij("Btw 21%", min(nota.totals.vat))}
    ${totRij("Totaal credit", min(nota.totals.total), true)}
  </table>

  ${blok("Verrekening", verrekeningTekst(order, nota, volledig))}
  ${blok("Administratieve verwijzing", `Deze creditnota vormt een correctie op en verwijst uitdrukkelijk naar factuur ${factuurNo}${order.createdAt ? ` d.d. ${datum(order.createdAt)}` : ""}. Bewaar beide documenten bij elkaar in de administratie.`)}

  <div style="margin-top:26px;color:#6b7280;font-size:12px;">${esc(COMPANY.legalName)} · Bedankt voor uw bestelling.</div>
  ${printHook}
</body></html>`;
}
